import React from "react";
import { color, space, text, weight, tracking, numeric, motion } from "../tokens";

/**
 * <DataTable> — dense B2B table.
 *
 *   columns  : [{ key, label, align?, width?, numeric?, render?(row) }]
 *   rows     : [ { key, ...data, onClick?, selected? } ]
 *   density  : "comfortable" (40 row) default | "dense" (32 row)
 *   sticky   : boolean — sticky header (default true)
 *
 * Rules baked in:
 *  - 13px text throughout
 *  - Left-align text, right-align numerics automatically
 *  - Numerics get tabular figures
 *  - Row hover: bg.subtle
 *  - Selected row: brand.subtle + 2px left border
 *  - No borders between rows below 200 rows (Attio pattern) — just 1px header divider
 */
export function DataTable({
  columns,
  rows,
  density = "comfortable",
  sticky = true,
  emptyState,
  style,
}) {
  const rowHeight = density === "dense" ? 32 : 40;
  if (!rows || rows.length === 0) {
    return (
      <div style={{ padding: space[8], textAlign: "center", color: color.text.tertiary, fontSize: text.base, ...style }}>
        {emptyState ?? "No data."}
      </div>
    );
  }
  return (
    <div style={{ overflowX: "auto", ...style }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.base, tableLayout: "auto" }}>
        <thead>
          <tr
            style={{
              background: color.bg.subtle,
              position: sticky ? "sticky" : "static",
              top: 0,
              zIndex: 1,
            }}
          >
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  padding: `${space[3]}px ${space[4]}px`,
                  textAlign: c.align || (c.numeric ? "right" : "left"),
                  width: c.width,
                  fontSize: text.xs,
                  fontWeight: weight.semibold,
                  color: color.text.tertiary,
                  letterSpacing: tracking.wider,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${color.border.subtle}`,
                  whiteSpace: "nowrap",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow key={row.key} row={row} columns={columns} rowHeight={rowHeight} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableRow({ row, columns, rowHeight }) {
  const [hover, setHover] = React.useState(false);
  const bg = row.selected
    ? color.brand.subtle
    : hover
    ? color.bg.subtle
    : "transparent";
  return (
    <tr
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={row.onClick}
      style={{
        background: bg,
        cursor: row.onClick ? "pointer" : "default",
        transition: motion.tx("background", "fast"),
        boxShadow: row.selected ? `inset 2px 0 0 ${color.brand.solid}` : "none",
      }}
    >
      {columns.map((c) => {
        const value = c.render ? c.render(row) : row[c.key];
        return (
          <td
            key={c.key}
            style={{
              padding: `0 ${space[4]}px`,
              height: rowHeight,
              textAlign: c.align || (c.numeric ? "right" : "left"),
              color: color.text.primary,
              verticalAlign: "middle",
              whiteSpace: "nowrap",
              ...(c.numeric ? numeric.tabular : {}),
            }}
          >
            {value}
          </td>
        );
      })}
    </tr>
  );
}
